import { useState, useRef, useEffect } from 'react';

export type FilterableClassification = 'optimal' | 'normal' | 'out_of_range' | 'critical';

interface BiomarkerFilterProps {
  availableClassifications: Set<FilterableClassification>;
  selectedFilter: FilterableClassification | null;
  onFilterChange: (classification: FilterableClassification | null) => void;
}

const classificationLabels: Record<FilterableClassification, string> = {
  optimal: 'Optimal',
  normal: 'Normal',
  out_of_range: 'Out of range',
  critical: 'Critical',
};

export function BiomarkerFilter({
  availableClassifications,
  selectedFilter,
  onFilterChange,
}: BiomarkerFilterProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isFilterOpen]);

  const toggleFilter = (classification: FilterableClassification) => {
    // If clicking the same filter, deselect it (show all)
    if (selectedFilter === classification) {
      onFilterChange(null);
    } else {
      // Otherwise select the clicked filter
      onFilterChange(classification);
    }
  };

  return (
    <div ref={filterDropdownRef} className="relative inline-block">
      <button
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-[16px] font-semibold leading-[130%] transition-colors ${
          isFilterOpen
            ? 'bg-[#183344] text-white'
            : 'bg-white text-[#183344] hover:bg-[#183344] hover:text-white'
        }`}
        type="button"
      >
        <span>Filter biomarkers</span>
        <svg
          className={`h-[18px] w-[18px] transition-transform ${isFilterOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isFilterOpen && (
        <div className="absolute left-0 mt-2 z-10 min-w-[250px] rounded-lg bg-white shadow-lg border border-[rgba(0,0,0,0.1)] py-2">
          {(['critical', 'out_of_range', 'normal', 'optimal'] as FilterableClassification[]).map((classification) => {
            if (!availableClassifications.has(classification)) return null;
            const isSelected = selectedFilter === classification;

            return (
              <button
                key={classification}
                onClick={() => toggleFilter(classification)}
                className="flex w-full items-center gap-[10px] cursor-pointer px-4 py-2 hover:bg-gray-50"
                type="button"
              >
                <div className="h-[18px] w-[18px] flex items-center justify-center">
                  {isSelected && (
                    <svg
                      className="h-[18px] w-[18px] text-[#183344]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="text-[16px] font-semibold leading-[130%] text-[#183344]">
                  {classificationLabels[classification]}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
